/**
 * API Service for ADK Agent Builder
 * Handles all backend communication for agent creation, testing, and MCP tools
 */

import { API_BASE, MCP_BASE } from '../utils/api';

const MCP_SERVER_URL = MCP_BASE;

// Types
export interface MCPTool {
  name: string;
  description: string;
}

export interface AgentInfo {
  id: string;
  name: string;
  description: string;
}

export interface ServiceAccountInfo {
  path: string;
  project: string;
  location: string;
  isValid: boolean;
}

export interface CreateAgentPayload {
  name: string;
  description?: string;
  model: string;
  instruction: string;
  temperature?: number;
  top_p?: number;
  max_output_tokens?: number;
  mcp?: {
    url: string;
    tool_filter: string[];
    headers?: Record<string, string>;
  };
  custom_tools?: Array<{
    name: string;
    description?: string;
    schema: string;
    codeRef?: string;
  }>;
  integrations?: {
    link_with_agents?: string[];
    allow_cross_agent_calls: boolean;
  };
  env?: Record<string, string>;
  memory_config?: {
    type: string;
    enabled: boolean;
    window_size?: number;
  };
  workflow?: {
    steps: Array<
      | { type: 'agent'; agent: string }
      | { type: 'parallel'; agents: string[] }
      | { type: 'loop'; agents: string[]; max_iterations?: number }
    >;
    agents: Array<{
      name: string;
      description?: string;
      instruction: string;
      model: string;
      temperature?: number;
      top_p?: number;
      max_output_tokens?: number;
      mcp?: {
        url: string;
        tool_filter: string[];
        headers?: Record<string, string>;
      };
    }>;
  };
}

export interface TestAgentPayload {
  prompt: string;
}

export interface WorkflowRecord {
  id: string;
  name: string;
  description?: string;
  lastModified: number;
  nodes: any[];
  connections: any[];
  deployed?: boolean;
  identifier?: string;
}

// API Functions

/**
 * Fetch available MCP tools from the backend
 */
export async function fetchMCPTools(url: string = MCP_SERVER_URL): Promise<MCPTool[]> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/mcp/tools?url=${encodeURIComponent(url)}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch MCP tools: ${response.statusText}`);
    }
    const tools = await response.json();
    return tools.filter((t: any) => t.name !== 'query_vector_db' && t.name !== 'search_vector_database');
  } catch (error) {
    console.error('Error fetching MCP tools:', error);
    // Return fallback tools from MCP router
    return [
      { name: 'analyze_file_with_prompt', description: 'Analyze files with custom prompts' },
      { name: 'get_weather', description: 'Get weather information' },
      { name: 'fetch_from_mail', description: 'Fetch emails from mailbox' },
      { name: 'match_resumes', description: 'Match resumes to job requirements' },
      { name: 'file_upload', description: 'Upload files to storage' },
      { name: 'ingest_local_resumes_to_metadata', description: 'Ingest local resumes to metadata store' },
      { name: 'manage_offer_letter_from_template', description: 'Generate offer letters from templates' },
      { name: 'get_policy_info', description: 'Get company policy information' },
      { name: 'schedule_interview', description: 'Schedule interviews via Google Calendar' },
      { name: 'manage_interview_records', description: 'Manage interview records in database' },
      { name: 'reconcile_resumes', description: 'Reconcile resume data' },
      { name: 'transcribe_media_to_insights', description: 'Transcribe audio/video to text insights' },
      { name: 'send_candidate_email', description: 'Send emails to candidates' },
      { name: 'manage_interview', description: 'Manage interview scheduling' },
      { name: 'generate_job_posting', description: 'Generate job postings from templates' },
      { name: 'get_application_status', description: 'Get application status updates' },
      { name: 'transfer_task', description: 'Transfer tasks between agents' }
    ];
  }
}

/**
 * Fetch available agents for linking
 */
export async function fetchAvailableAgents(): Promise<AgentInfo[]> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/agents`);
    if (!response.ok) {
      throw new Error(`Failed to fetch agents: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching agents:', error);
    return [];
  }
}

/**
 * Check service account configuration
 */
export async function checkServiceAccount(): Promise<ServiceAccountInfo> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/service-account`);
    if (!response.ok) {
      throw new Error(`Failed to check service account: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error checking service account:', error);
    return {
      path: '',
      project: '',
      location: '',
      isValid: false
    };
  }
}

/**
 * Create a new agent
 * @param payload - Agent configuration
 * @param getOrCreate - If true, returns existing agent instead of throwing error
 */
export async function createAgent(
  payload: CreateAgentPayload,
  getOrCreate: boolean = false,
  updateIfExists: boolean = false
): Promise<{ name: string; identifier: string }> {
  const url = new URL(`${API_BASE}/agent-builder/agents`);
  if (getOrCreate) {
    url.searchParams.set('get_or_create', 'true');
  }
  if (updateIfExists) {
    url.searchParams.set('update_if_exists', 'true');
  }

  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to create agent: ${error}`);
  }

  return await response.json();
}
/**
 * Test an agent with a prompt
 */
export async function testAgent(agentId: string, prompt: string): Promise<{ ok: boolean; response: any }> {
  const response = await fetch(`${API_BASE}/agent-builder/agents/${agentId}/test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ prompt }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to test agent: ${error}`);
  }

  return await response.json();
}

/**
 * Test a workflow by running through all nodes
 */
export async function testWorkflow(
  nodes: any[], 
  connections: any[], 
  input: string,
  file_data?: { name: string; type: string; base64: string } | null
): Promise<{ success: boolean; results: any[]; response?: string; error?: string; note?: string }> {
  try {
    // Use standard workflow test endpoint
    const response = await fetch(`${API_BASE}/agent-builder/workflow/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        nodes, 
        connections, 
        input,
        file_data: file_data || undefined
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      return { success: false, results: [], error };
    }

    return await response.json();
  } catch (error: any) {
    return { success: false, results: [], error: error.message };
  }
}

/**
 * Save workflow as a generated agent
 */
export async function saveWorkflow(
  workflowName: string,
  nodes: any[],
  connections: any[]
): Promise<{ success: boolean; identifier?: string; error?: string }> {
  // Extract configuration from nodes
  const startNode = nodes.find(n => n.type === 'start');
  const agentNodes = nodes.filter((n: any) => n.type === 'agent_engine');
  const agentById = new Map(agentNodes.map((n: any) => [n.id, n]));

  const startConnection = startNode
    ? connections.find((c: any) => c.source === startNode.id && c.targetType === 'agent_engine')
    : null;
  const primaryAgentNode = startConnection
    ? agentById.get(startConnection.target)
    : agentNodes[0];
  const agentNode = primaryAgentNode;
  const modelNode = nodes.find(n => n.type === 'llm');
  const memoryNode = nodes.find(n => n.type === 'memory');
  const mcpNode = nodes.find(n => n.type === 'mcp');

  if (!agentNode) {
    return { success: false, error: 'No Agent Engine node found in workflow' };
  }

  const normalizeAgentName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return '';
    return trimmed;
  };

  const getConnectedNodes = (targetId: string, sourceType: string) => {
    return connections
      .filter((c: any) => c.target === targetId && c.sourceType === sourceType)
      .map((c: any) => nodes.find((n: any) => n.id === c.source))
      .filter(Boolean);
  };

  const getConnectedNode = (targetId: string, sourceType: string) => {
    const linked = getConnectedNodes(targetId, sourceType);
    return linked.length > 0 ? linked[0] : null;
  };

  const agentSpecs = agentNodes.map((node: any) => {
    const nodeModel = getConnectedNode(node.id, 'llm') || modelNode;
    const nodeMcp = getConnectedNode(node.id, 'mcp');
    const dataConnectors = getConnectedNodes(node.id, 'data_connector');

    // Auto-wire tools from data connectors
    let autoWiredTools: string[] = [];
    let dbContextLines: string[] = [];
    dataConnectors.forEach((dc: any) => {
      if (dc.data.connectorStrategy === 'vector_db') autoWiredTools.push('search_vector_database');
      if (dc.data.connectorStrategy === 'knowledge_graph') autoWiredTools.push('query_knowledge_graph');
      
      if (dc.data.allowLiveRead) {
        if (dc.data.connectorProvider === 'sharepoint') autoWiredTools.push('list_sharepoint_files', 'read_sharepoint_document_live');
        if (dc.data.connectorProvider === 'google_sheets') autoWiredTools.push('read_sheet_range');
        if (dc.data.connectorProvider === 'postgres' || dc.data.connectorProvider === 'oracle') autoWiredTools.push('list_database_tables');
      }
      
      if (dc.data.allowLiveWrite) {
        if (dc.data.connectorProvider === 'google_sheets') autoWiredTools.push('append_sheet_row');
        if (dc.data.connectorProvider === 'postgres' || dc.data.connectorProvider === 'oracle') autoWiredTools.push('execute_live_sql_query');
      }

      // Build DB context for the agent's instruction
      if (dc.data.connectorProvider === 'postgres' || dc.data.connectorProvider === 'oracle') {
        try {
          const creds = JSON.parse(dc.data.connectorCredentials || '{}');
          if (creds.host) {
            const rawKey = `${creds.host}:${creds.port || 5432}/${creds.dbname || 'postgres'}`;
            // Simple string hashing for the vector_key fallback, to match the backend expectation
            // In a real scenario we could fetch the exact key from the chunking response, but the
            // backend uses md5. For now, we instruct the agent.
            const connJson = JSON.stringify({
              host: creds.host,
              user: creds.user,
              password: creds.password,
              dbname: creds.dbname || 'postgres',
              port: creds.port || 5432,
            });
            dbContextLines.push(
              `\n\n[DATABASE CONNECTION]\n` +
              `You have access to a ${dc.data.connectorProvider === 'postgres' ? 'PostgreSQL' : 'Oracle'} database.\n` +
              `When using list_database_tables or execute_live_sql_query tools, pass this as the connection_id parameter: ${connJson}\n` +
              (dc.data.chunkedTables && dc.data.chunkedTables.length > 0 ? `The following tables are available and indexed: ${dc.data.chunkedTables.join(', ')}.\n` : '') +
              (dc.data.connectorStrategy === 'vector_db' ? `Vector Indexing is ENABLED for this database. Use search_vector_database tool for semantic RAG queries. Pass vector_key as empty string ("") to search all connected data unless specified.\n` : '') +
              `You CAN list tables, count them, describe schemas, and run SELECT queries on this database. Use these tools proactively when the user asks about their data.`
            );
          }
        } catch {}
      }

      // Google Sheets context
      if (dc.data.connectorProvider === 'google_sheets' && dc.data.connectorTarget) {
        dbContextLines.push(
          `\n\n[GOOGLE SHEETS CONNECTION]\n` +
          `You have access to a Google Spreadsheet. The spreadsheet ID is: ${dc.data.connectorTarget}\n` +
          `When using read_sheet_range or append_sheet_row, pass this spreadsheet_id.`
        );
      }

      // SharePoint context
      if (dc.data.connectorProvider === 'sharepoint' && dc.data.connectorTarget) {
        dbContextLines.push(
          `\n\n[SHAREPOINT CONNECTION]\n` +
          `You have access to a SharePoint site. The site ID is: ${dc.data.connectorTarget}\n` +
          `When using list_sharepoint_files or read_sharepoint_document_live, pass this site_id.`
        );
      }
    });

    const mcpTools = Array.from(new Set([
      ...(nodeMcp?.data?.selectedMcpTools || []),
      ...autoWiredTools
    ]));

    // Build final instruction with DB context appended
    const baseInstruction = node.data.systemPrompt || 'You are a helpful assistant.';
    const finalInstruction = dbContextLines.length > 0
      ? baseInstruction + dbContextLines.join('')
      : baseInstruction;

    const spec = {
      name: node.data.agentName || workflowName,
      description: node.data.description || '',
      instruction: finalInstruction,
      model: nodeModel?.data?.modelName || 'gemini-2.0-flash-exp',
      temperature: nodeModel?.data?.temperature,
      top_p: nodeModel?.data?.topP,
      max_output_tokens: nodeModel?.data?.maxOutputTokens,
      mcp: mcpTools.length
        ? {
            url: nodeMcp?.data?.mcpServerUrl || MCP_SERVER_URL,
            tool_filter: mcpTools,
          }
        : undefined,
    };
    return spec;
  });

  const linkedAgents: string[] = [];
  const seenAgents = new Set<string>();
  const adjacency = new Map<string, string[]>();

  connections.forEach((c: any) => {
    if (c.sourceType === 'agent_engine' && c.targetType === 'agent_engine') {
      const list = adjacency.get(c.source) || [];
      list.push(c.target);
      adjacency.set(c.source, list);
    }
  });

  const traverseQueue: string[] = agentNode ? [agentNode.id] : [];
  const visitedNodes = new Set<string>(); // Track visited nodes to prevent infinite loops
  while (traverseQueue.length > 0) {
    const current = traverseQueue.shift();
    if (!current) continue;
    if (visitedNodes.has(current)) continue; // Skip if already visited (handles cycles)
    visitedNodes.add(current);
    
    const nextIds = adjacency.get(current) || [];
    nextIds.forEach((nextId) => {
      const nextNode = agentById.get(nextId);
      const agentName = nextNode?.data?.agentName || '';
      const normalized = normalizeAgentName(agentName);
      if (normalized && !seenAgents.has(normalized)) {
        seenAgents.add(normalized);
        linkedAgents.push(normalized);
      }
      if (!visitedNodes.has(nextId)) {
        traverseQueue.push(nextId);
      }
    });
  }

  const agentNameById = (id: string) => {
    const node = agentById.get(id);
    return node?.data?.agentName ? normalizeAgentName(node.data.agentName) : '';
  };

  const steps: Array<
    | { type: 'agent'; agent: string }
    | { type: 'parallel'; agents: string[] }
    | { type: 'loop'; agents: string[]; max_iterations?: number }
  > = [];

  if (agentNode) {
    const path: string[] = [];
    let currentId: string | undefined = agentNode.id;

    while (currentId) {
      const existingIndex = path.indexOf(currentId);
      if (existingIndex !== -1) {
        const cycleIds = path.slice(existingIndex);
        const cycleAgents = cycleIds.map(agentNameById).filter(Boolean);
        const maxIterations =
          agentById.get(cycleIds[0])?.data?.maxIterations || 3;
        if (cycleAgents.length > 0) {
          steps.splice(steps.length - cycleAgents.length, cycleAgents.length);
          steps.push({ type: 'loop', agents: cycleAgents, max_iterations: maxIterations });
        }
        break;
      }

      path.push(currentId);
      const currentName = agentNameById(currentId);
      if (currentName) {
        steps.push({ type: 'agent', agent: currentName });
      }

      const outgoing = adjacency.get(currentId) || [];
      if (outgoing.length === 0) {
        break;
      }

      if (outgoing.length === 1) {
        currentId = outgoing[0];
        continue;
      }

      const parallelAgents = outgoing.map(agentNameById).filter(Boolean);
      if (parallelAgents.length > 0) {
        steps.push({ type: 'parallel', agents: parallelAgents });
      }

      const nextTargets = outgoing.map((id) => adjacency.get(id) || []);
      const common = nextTargets.length
        ? new Set(nextTargets.map((list) => (list.length === 1 ? list[0] : '')))
        : new Set();
      const commonValues = Array.from(common) as string[];
      if (common.size === 1 && commonValues[0]) {
        currentId = commonValues[0];
        continue;
      }

      break;
    }
  }

  const primarySpecIndex = agentNodes.findIndex((n: any) => n.id === agentNode.id);
  const primarySpec = agentSpecs[primarySpecIndex !== -1 ? primarySpecIndex : 0];

  const payload: CreateAgentPayload = {
    name: primarySpec.name,
    description: primarySpec.description,
    model: primarySpec.model,
    instruction: primarySpec.instruction,
    temperature: primarySpec.temperature,
    top_p: primarySpec.top_p,
    max_output_tokens: primarySpec.max_output_tokens,
  };

  if (primarySpec.mcp) {
    payload.mcp = primarySpec.mcp;
  }

  if (linkedAgents.length > 0) {
    const baseInstruction = payload.instruction?.trim() || 'You are a helpful assistant.';
    const handoffHint = `After completing your task, transfer to the next agent in the workflow: ${linkedAgents.join(', ')}.`;
    payload.instruction = `${baseInstruction}\n\n${handoffHint}`;
    payload.integrations = {
      link_with_agents: linkedAgents,
      allow_cross_agent_calls: true,
    };
  }

  if (steps.length > 1 || steps.some(step => step.type !== 'agent')) {
    payload.workflow = {
      steps,
      agents: agentSpecs,
    };
  }

  // Add memory configuration
  if (memoryNode?.data.memoryEnabled) {
    payload.memory_config = {
      type: memoryNode.data.memoryType || 'vertex_ai',
      enabled: true,
      window_size: memoryNode.data.windowSize,
    };
  }

  try {
    // Use getOrCreate=true so it returns existing agent instead of error
    const result = await createAgent(payload, true, true);
    return { success: true, identifier: result.identifier };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

/**
 * Fetch saved workflows from the backend.
 */
export async function fetchWorkflows(): Promise<WorkflowRecord[]> {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE}/agent-builder/workflows`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch workflows: ${response.statusText}`);
  }
  return await response.json();
}

/**
 * Create or update a workflow record in the backend.
 */
export async function upsertWorkflow(record: WorkflowRecord): Promise<WorkflowRecord> {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE}/agent-builder/workflows`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    body: JSON.stringify(record),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to save workflow: ${error}`);
  }

  return await response.json();
}

/**
 * Delete a workflow record from the backend.
 */
export async function deleteWorkflow(workflowId: string): Promise<void> {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE}/agent-builder/workflows/${workflowId}`, {
    method: 'DELETE',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to delete workflow: ${error}`);
  }
}

/**
 * Fetch deployed agents from the backend.
 */
export async function fetchDeployedAgents(): Promise<Array<{ id?: string; name: string; identifier: string; description: string }>> {
  const response = await fetch(`${API_BASE}/agent-builder/deployed-agents`);
  if (!response.ok) {
    throw new Error(`Failed to fetch deployed agents: ${response.statusText}`);
  }
  return await response.json();
}

export const deployedAgentsStreamUrl = `${API_BASE}/agent-builder/deployed-agents/stream`;

/**
 * Undeploy an agent by identifier.
 */
export async function undeployAgent(identifier: string): Promise<void> {
  const response = await fetch(`${API_BASE}/agent-builder/deployed-agents/${identifier}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to undeploy agent: ${error}`);
  }
}

/**
 * Validate workflow structure
 */
export function validateWorkflow(nodes: any[], connections: any[]): { 
  isValid: boolean; 
  errors: string[]; 
  warnings: string[] 
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check for start node or trigger node
  const startNode = nodes.find(n => n.type === 'start' || n.type === 'trigger');
  if (!startNode) {
    errors.push('Workflow must have a Start or Trigger node');
  }

  // Check for end node
  const endNode = nodes.find(n => n.type === 'end');
  if (!endNode) {
    errors.push('Workflow must have an End node');
  }

  // Check for agent engine node
  const agentNode = nodes.find(n => n.type === 'agent_engine');
  if (!agentNode) {
    errors.push('Workflow must have an Agent Engine node');
  } else {
    // Validate agent engine has required fields
    if (!agentNode.data.agentName?.trim()) {
      errors.push('Agent Engine must have a name');
    }
    if (!agentNode.data.systemPrompt?.trim()) {
      warnings.push('Agent Engine should have a system prompt');
    }
  }

  // Check connections - start must connect to something
  if (startNode) {
    const startConnections = connections.filter(c => c.source === startNode.id);
    if (startConnections.length === 0) {
      errors.push('Start node must be connected to the workflow');
    }
  }

  // Check connections - end must receive from something
  if (endNode) {
    const endConnections = connections.filter(c => c.target === endNode.id);
    if (endConnections.length === 0) {
      errors.push('End node must receive connections from the workflow');
    }
  }

  // Check model config
  const modelNode = nodes.find(n => n.type === 'llm');
  if (!modelNode) {
    warnings.push('No Model Config node - default model will be used');
  } else if (!modelNode.data.modelName) {
    warnings.push('Model Config should have a model selected');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

// =============================================================================
// TOOL AGENTS MARKETPLACE API
// =============================================================================

export interface ToolAgentInfo {
  id: string;
  name: string;
  description: string;
  instruction: string;
  model: string;
  has_tools: boolean;
  source: 'builtin' | 'file' | 'generated';
  config_fields?: ToolAgentConfigField[];
  has_required_config?: boolean;
}

export interface ToolAgentConfigField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'textarea' | 'password' | 'file' | 'email';
  required?: boolean;
  default?: string | number;
  description?: string;
  options?: string[];
}

/**
 * Fetch available tool agents from the marketplace
 */
export async function fetchToolAgents(): Promise<ToolAgentInfo[]> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/tool-agents`);
    if (!response.ok) {
      console.warn('Failed to fetch tool agents, using fallback');
      return [];
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching tool agents:', error);
    return [];
  }
}

/**
 * Fetch configuration requirements for a specific tool agent
 */
export async function fetchToolAgentConfig(agentId: string): Promise<{ agent_id: string; fields: ToolAgentConfigField[]; has_required: boolean }> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/tool-agents/${agentId}/config`);
    if (!response.ok) {
      return { agent_id: agentId, fields: [], has_required: false };
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching tool agent config:', error);
    return { agent_id: agentId, fields: [], has_required: false };
  }
}

// =============================================================================
// MEMORY CONFIGURATION API
// =============================================================================

export interface MemoryCredentialField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'file' | 'number';
  default?: string | number;
  description: string;
}

export interface MemoryProvider {
  id: string;
  name: string;
  description: string;
  cloud: string | null;
  required_credentials: MemoryCredentialField[];
  env_vars: string[];
  auto_configured: boolean;
}

export interface MemoryVerifyResult {
  success: boolean;
  provider: string;
  message: string;
  details?: Record<string, any>;
}

export interface MemoryStatus {
  enabled: boolean;
  available: boolean;
  provider: string | null;
  config?: {
    project?: string;
    location?: string;
    agent_engine_id?: string;
  };
  error?: string;
}

/**
 * Fetch available memory providers with their configuration requirements
 */
export async function fetchMemoryProviders(): Promise<MemoryProvider[]> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/memory/providers`);
    if (!response.ok) {
      throw new Error('Failed to fetch memory providers');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching memory providers:', error);
    // Return default providers if API fails
    return [
      {
        id: 'vertex_ai',
        name: 'Google Vertex AI Memory Bank',
        description: 'Cloud-based long-term memory using Google Vertex AI',
        cloud: 'GCP',
        required_credentials: [],
        env_vars: [],
        auto_configured: true
      },
      {
        id: 'local_json',
        name: 'Local JSON Storage',
        description: 'File-based local storage for development',
        cloud: null,
        required_credentials: [],
        env_vars: [],
        auto_configured: true
      },
      {
        id: 'window_buffer',
        name: 'Window Buffer',
        description: 'In-memory conversation buffer',
        cloud: null,
        required_credentials: [],
        env_vars: [],
        auto_configured: true
      }
    ];
  }
}

/**
 * Verify memory provider configuration
 */
export async function verifyMemoryProvider(
  provider: string,
  credentials?: Record<string, string>
): Promise<MemoryVerifyResult> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/memory/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, credentials })
    });
    
    if (!response.ok) {
      return {
        success: false,
        provider,
        message: 'Failed to verify memory provider'
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      provider,
      message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Get current memory status from backend
 */
export async function getMemoryStatus(): Promise<MemoryStatus> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/memory/status`);
    if (!response.ok) {
      return { enabled: false, available: false, provider: null };
    }
    return await response.json();
  } catch (error) {
    return { enabled: false, available: false, provider: null, error: String(error) };
  }
}

/**
 * Get memory environment configuration from backend
 */
export interface MemoryEnvConfig {
  vertex_ai: {
    project_id: string;
    location: string;
    agent_engine_id: string;
    service_account_path: string;
    service_account_valid: boolean;
    configured: boolean;
  };
  local_json: {
    storage_path: string;
    configured: boolean;
  };
  window_buffer: {
    window_size: number;
    configured: boolean;
  };
}

export async function getMemoryEnvConfig(): Promise<MemoryEnvConfig | null> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/memory/env-config`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching memory env config:', error);
    return null;
  }
}


/**
 * Test a single node with given input
 */
export interface NodeTestPayload {
  node_type: string;
  node_data: Record<string, any>;
  input: string;
  file_data?: {
    name: string;
    type: string;
    base64: string;
  };
}

export async function testSingleNode(payload: NodeTestPayload): Promise<{ success: boolean; output: string; error?: string }> {
  try {
    const response = await fetch(`${API_BASE}/agent-builder/node/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const error = await response.text();
      return { success: false, output: '', error };
    }
    
    return await response.json();
  } catch (error) {
    return { success: false, output: '', error: String(error) };
  }
}


// =============================================================================
// DATA CONNECTOR INTEGRATION API
// =============================================================================

export interface IntegrationStatus {
  connected: boolean;
  email?: string;
  provider: string;
}

export interface GoogleSheet {
  id: string;
  name: string;
  modified: string;
  owner: string;
}

export interface SheetTab {
  id: number;
  title: string;
  index: number;
  rowCount: number;
  columnCount: number;
}

export interface SharePointSite {
  id: string;
  name: string;
  webUrl: string;
}

export interface SharePointDrive {
  id: string;
  name: string;
  webUrl: string;
}

/**
 * Get Google OAuth consent URL
 */
export async function getGoogleAuthUrl(userId: string = 'default_user'): Promise<string> {
  const response = await fetch(`${API_BASE}/api/integrations/google/auth-url?user_id=${userId}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || 'Failed to get auth URL');
  }
  const data = await response.json();
  return data.auth_url;
}

/**
 * Check Google Sheets connection status
 */
export async function getGoogleStatus(userId: string = 'default_user'): Promise<IntegrationStatus> {
  try {
    const response = await fetch(`${API_BASE}/api/integrations/google/status?user_id=${userId}`);
    if (!response.ok) return { connected: false, provider: 'google_sheets' };
    return await response.json();
  } catch {
    return { connected: false, provider: 'google_sheets' };
  }
}

/**
 * Disconnect Google Sheets
 */
export async function disconnectGoogle(userId: string = 'default_user'): Promise<void> {
  await fetch(`${API_BASE}/api/integrations/google/disconnect?user_id=${userId}`, { method: 'POST' });
}

/**
 * List available Google Sheets
 */
export async function listGoogleSheets(userId: string = 'default_user'): Promise<GoogleSheet[]> {
  const response = await fetch(`${API_BASE}/api/integrations/google/sheets?user_id=${userId}`);
  if (!response.ok) throw new Error('Failed to list sheets');
  const data = await response.json();
  return data.sheets || [];
}

export interface GoogleFolder {
  id: string;
  name: string;
  modified: string;
  owner: string;
}

/**
 * List available Google Drive Folders
 */
export async function listGoogleFolders(userId: string = 'default_user'): Promise<GoogleFolder[]> {
  const response = await fetch(`${API_BASE}/api/integrations/google/folders?user_id=${userId}`);
  if (!response.ok) throw new Error('Failed to list folders');
  const data = await response.json();
  return data.folders || [];
}


/**
 * List tabs in a specific Google Spreadsheet
 */
export async function listSheetTabs(spreadsheetId: string, userId: string = 'default_user'): Promise<SheetTab[]> {
  const response = await fetch(`${API_BASE}/api/integrations/google/sheets/${spreadsheetId}/tabs?user_id=${userId}`);
  if (!response.ok) throw new Error('Failed to list tabs');
  const data = await response.json();
  return data.tabs || [];
}

/**
 * Get Microsoft OAuth consent URL
 */
export async function getMicrosoftAuthUrl(userId: string = 'default_user'): Promise<string> {
  const response = await fetch(`${API_BASE}/api/integrations/microsoft/auth-url?user_id=${userId}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || 'Failed to get auth URL');
  }
  const data = await response.json();
  return data.auth_url;
}

/**
 * Check Microsoft/SharePoint connection status
 */
export async function getMicrosoftStatus(userId: string = 'default_user'): Promise<IntegrationStatus> {
  try {
    const response = await fetch(`${API_BASE}/api/integrations/microsoft/status?user_id=${userId}`);
    if (!response.ok) return { connected: false, provider: 'sharepoint' };
    return await response.json();
  } catch {
    return { connected: false, provider: 'sharepoint' };
  }
}

/**
 * Disconnect Microsoft/SharePoint
 */
export async function disconnectMicrosoft(userId: string = 'default_user'): Promise<void> {
  await fetch(`${API_BASE}/api/integrations/microsoft/disconnect?user_id=${userId}`, { method: 'POST' });
}

/**
 * List SharePoint sites
 */
export async function listSharePointSites(userId: string = 'default_user'): Promise<SharePointSite[]> {
  const response = await fetch(`${API_BASE}/api/integrations/microsoft/sites?user_id=${userId}`);
  if (!response.ok) throw new Error('Failed to list sites');
  const data = await response.json();
  return data.sites || [];
}

/**
 * List drives in a SharePoint site
 */
export async function listSiteDrives(siteId: string, userId: string = 'default_user'): Promise<SharePointDrive[]> {
  const response = await fetch(`${API_BASE}/api/integrations/microsoft/sites/${siteId}/drives?user_id=${userId}`);
  if (!response.ok) throw new Error('Failed to list drives');
  const data = await response.json();
  return data.drives || [];
}

// =============================================================================
// PostgreSQL / Direct DB
// =============================================================================

export interface DBTestResult {
  success: boolean;
  message: string;
  version?: string;
  table_count?: number;
  db_size?: string;
}

export interface DBTable {
  name: string;
  rows: number;
  size: string;
  columns: Array<{ name: string; type: string; nullable: boolean }>;
  column_count: number;
}

export interface DBTablesResult {
  success: boolean;
  message?: string;
  database?: string;
  host?: string;
  table_count?: number;
  tables: DBTable[];
}

/**
 * Test a PostgreSQL database connection
 */
export async function testDBConnection(creds: {
  host: string; user: string; password: string; dbname?: string; port?: number;
}): Promise<DBTestResult> {
  try {
    const response = await fetch(`${API_BASE}/api/integrations/db/test-connection`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        host: creds.host,
        user: creds.user,
        password: creds.password,
        dbname: creds.dbname || 'postgres',
        port: creds.port || 5432,
      }),
    });
    return await response.json();
  } catch (e: any) {
    return { success: false, message: e.message || 'Connection test failed' };
  }
}

/**
 * List tables in a PostgreSQL database
 */
export async function listDBTables(creds: {
  host: string; user: string; password: string; dbname?: string; port?: number;
}): Promise<DBTablesResult> {
  try {
    const response = await fetch(`${API_BASE}/api/integrations/db/tables`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        host: creds.host,
        user: creds.user,
        password: creds.password,
        dbname: creds.dbname || 'postgres',
        port: creds.port || 5432,
      }),
    });
    return await response.json();
  } catch (e: any) {
    return { success: false, message: e.message || 'Failed to list tables', tables: [] };
  }
}

// =============================================================================
// Chunking Pipeline (Vertex AI)
// =============================================================================

export interface ChunkStatus {
  status: 'started' | 'running' | 'done' | 'error' | 'not_found' | 'already_running';
  key?: string;
  phase?: string;
  progress?: number;
  total_chunks?: number;
  total_tables?: number;
  tables_processed?: string[];
  message?: string;
}

/**
 * Start the chunking + embedding pipeline for a connected DB
 */
export async function startChunking(creds: {
  host: string; user: string; password: string; dbname?: string; port?: number;
  tables?: string[];
}): Promise<ChunkStatus> {
  try {
    const response = await fetch(`${API_BASE}/api/integrations/db/chunk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        host: creds.host,
        user: creds.user,
        password: creds.password,
        dbname: creds.dbname || 'postgres',
        port: creds.port || 5432,
        tables: creds.tables || [],
      }),
    });
    return await response.json();
  } catch (e: any) {
    return { status: 'error', message: e.message || 'Failed to start chunking' };
  }
}

/**
 * Get the status of a chunking job
 */
export async function getChunkStatus(key: string): Promise<ChunkStatus> {
  try {
    const response = await fetch(`${API_BASE}/api/integrations/db/chunk-status?key=${key}`);
    return await response.json();
  } catch (e: any) {
    return { status: 'error', message: e.message || 'Failed to get status' };
  }
}

/**
 * Start the chunking + embedding pipeline for OAuth providers (Google Sheets / SharePoint)
 */
export async function startOAuthChunking(params: {
  provider: 'google_sheets' | 'sharepoint';
  targets: string[];    // spreadsheet IDs or site IDs
  target_names?: string[]; // display names for reference
  user_id?: string;
}): Promise<ChunkStatus> {
  try {
    const response = await fetch(`${API_BASE}/api/integrations/oauth/chunk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        provider: params.provider,
        targets: params.targets,
        target_names: params.target_names || [],
        user_id: params.user_id || 'default_user',
      }),
    });
    return await response.json();
  } catch (e: any) {
    return { status: 'error', message: e.message || 'Failed to start chunking' };
  }
}

/**
 * Get the status of an OAuth chunking job
 */
export async function getOAuthChunkStatus(key: string): Promise<ChunkStatus> {
  try {
    const response = await fetch(`${API_BASE}/api/integrations/oauth/chunk-status?key=${key}`);
    return await response.json();
  } catch (e: any) {
    return { status: 'error', message: e.message || 'Failed to get status' };
  }
}

